// @ts-nocheck
import ShareModal from './ShareModal';
import EmbedModal from './EmbedModal';
import DeleteModal from '../DeleteModal/DeleteModal';
import globalConfig from '../../../config/globalConfig';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  MenuItem,
  Divider,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  ShareNetwork,
  Paperclip,
  Eye,
  DotsThreeVertical,
  CaretDown,
  Code,
  Copy,
  Trash,
  FileArrowDown,
  FileText
} from '@phosphor-icons/react';
import { XGButton } from 'xg-cl';
import { SuccessToast, ErrorToast } from '../../../utils/alerts';
import { deleteTrip, cloneTrip } from '../../../services/trip/trip';
import request from '../../../services/request';
import PropTypes from 'prop-types';
import './NewTripHeaderActions.css';

const NewTripHeaderActions = ({ id, isPublic, setIsPublic }) => {
  const trip = useSelector((state) => state.trip);
  const navigate = useNavigate();
  const { t } = useTranslation(['trip', 'common', 'embed']);

  // State management
  const [shareAnchor, setShareAnchor] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [previewAnchor, setPreviewAnchor] = useState(null);
  const [kebabAnchor, setKebabAnchor] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalType, setShareModalType] = useState('proposal'); // 'proposal' | 'final'
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState('proposal'); // 'proposal' | 'final'
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [pdfIsLoading, setPdfIsLoading] = useState(false);
  const [exportingType, setExportingType] = useState(''); // Track which export is loading

  const { tripid, tripkey = '' } = trip;

  // Load preview mode from localStorage on component mount
  useEffect(() => {
    const savedMode = localStorage.getItem(`previewMode_${tripid}`);
    if (savedMode && ['proposal', 'final'].includes(savedMode)) {
      setPreviewMode(savedMode);
    }
  }, [tripid]);

  // Save preview mode to localStorage when it changes
  const handlePreviewModeChange = (mode) => {
    setPreviewMode(mode);
    localStorage.setItem(`previewMode_${tripid}`, mode);
    setPreviewAnchor(null);

    const url =
      mode === 'proposal'
        ? `/itineraries/${tripid}`
        : `/app/${tripid}/?uuid=${tripkey}`;
    window.open(url, '_blank');
  };

  // Event handlers for dropdown menus
  const handleShareClick = (event) => setShareAnchor(event.currentTarget);
  const handleExportClick = (event) => {
    // Prevent opening if PDF is loading
    if (pdfIsLoading) return;
    setExportAnchor(event.currentTarget);
  };
  const handlePreviewClick = (event) => {
    // If clicked on main button (not dropdown), open preview
    if (event.target.closest('.preview-dropdown-trigger')) {
      setPreviewAnchor(event.currentTarget);
    } else {
      openPreview();
    }
  };
  const handleKebabClick = (event) => setKebabAnchor(event.currentTarget);

  const closeMenus = () => {
    setShareAnchor(null);
    setExportAnchor(null);
    setPreviewAnchor(null);
    setKebabAnchor(null);
  };

  const handleShareProposal = () => {
    setShareModalType('proposal');
    setShareModalOpen(true);
    closeMenus();
  };

  const handleShareFinal = () => {
    setShareModalType('final');
    setShareModalOpen(true);
    closeMenus();
  };

  const handleEmbedCode = () => {
    setEmbedModalOpen(true);
    closeMenus();
  };

  const exportItineraryPDF = async () => {
    try {
      setPdfIsLoading(true);
      setExportingType('proposal');

      const response = await request.get(`/output/trip/${tripid}/pdf`, {
        timeout: 120000
      });

      if (response?.data && Object.keys(response?.data).length > 0) {
        const downloadPdfUrl = response?.data.files[0]?.url;
        window.open(downloadPdfUrl);
      } else {
        ErrorToast(t('errors:tripPdfCouldNotBeGenerated'));
      }
    } catch (error) {
      ErrorToast(t('errors:tripPdfCouldNotBeGenerated'));
    } finally {
      setPdfIsLoading(false);
      setExportingType('');
    }
  };

  const exportFinalItineraryPDF = async () => {
    try {
      setPdfIsLoading(true);
      setExportingType('final');

      const response = await request.get(
        `/output/trip/${tripid}/travel-doc-pdf`,
        {
          timeout: 2400000
        }
      );

      if (response?.data && Object.keys(response?.data).length > 0) {
        const downloadPdfUrl = response?.data.file?.url;

        const downloadUrl = await fetch(downloadPdfUrl);

        // Convert response in a Blob
        const blob = await downloadUrl.blob();

        // Create an URL from the Blob
        const url = URL.createObjectURL(blob);

        // Create a temporal link and force forzar the download
        const a = document.createElement('a');
        a.href = url;
        // Name of the file
        a.download = globalConfig.isProduction
          ? downloadPdfUrl.split('?').shift().split('/').pop()
          : downloadPdfUrl.split('/').pop();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Release created URL
        URL.revokeObjectURL(url);
      } else {
        ErrorToast(t('errors:tripPdfCouldNotBeGenerated'));
      }
    } catch (error) {
      ErrorToast(t('errors:tripPdfCouldNotBeGenerated'));
    } finally {
      setPdfIsLoading(false);
      setExportingType('');
    }
  };

  const exportPdfAttachments = async () => {
    try {
      setPdfIsLoading(true);
      setExportingType('attachments');

      const response = await request.get(
        `/output/trip/${tripid}/attachment-pdf`,
        {
          timeout: 120000
        }
      );

      if (response?.body && Object.keys(response?.body).length > 0) {
        const contentDisposition =
          response?.headers['Content-Disposition'] ||
          response?.headers['content-disposition'];
        if (!contentDisposition) return null;

        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        const newFileName = match ? match[1] : 'attachments.pdf';

        const uint8Array = new Uint8Array(response.body);

        const blob = new Blob([uint8Array], {
          type: 'application/pdf'
        });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = newFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Release created URL
        URL.revokeObjectURL(url);
      } else {
        ErrorToast(t('errors:tripPdfCouldNotBeGeneratedAttachments'));
      }
    } catch (error) {
      ErrorToast(t('errors:tripPdfCouldNotBeGeneratedAttachments'));
    } finally {
      setPdfIsLoading(false);
      setExportingType('');
    }
  };

  // Handle proposal PDF export
  const handleExportProposalPDF = async () => {
    closeMenus();
    await exportItineraryPDF();
  };

  // Handle final itinerary PDF export
  const handleExportFinalPDF = async () => {
    closeMenus();
    await exportFinalItineraryPDF();
  };

  // Handle attachments PDF export
  const handleExportAttachments = async () => {
    closeMenus();
    await exportPdfAttachments();
  };

  // Preview functionality
  const openPreview = () => {
    const url =
      previewMode === 'proposal'
        ? `/itineraries/${tripid}`
        : `/app/${tripid}/?uuid=${tripkey}`;
    window.open(url, '_blank');
  };

  // Kebab menu functionality
  const handleCopyTrip = async () => {
    try {
      setIsCopying(true);
      const { data } = await cloneTrip(tripid);
      if (data?.newtripid) {
        navigate(`/trips/${data?.newtripid}`);
      } else {
        ErrorToast(t('errors:tripCouldNotBeCopied'));
      }
    } catch (error) {
      ErrorToast(t('errors:tripCouldNotBeCopied'));
    } finally {
      setIsCopying(false);
    }
    closeMenus();
  };

  const handleDeleteTrip = () => {
    setDeleteModalOpen(true);
    closeMenus();
  };

  const confirmDeleteTrip = async () => {
    try {
      setIsDeleting(true);
      await deleteTrip(tripid);
      navigate('/trips');
      SuccessToast(t('trip:tripDeleted'));
    } catch (error) {
      ErrorToast(t('errors:deleteFailed'));
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  return (
    <div className='new-trip-header-actions' id={id}>
      {/* Share Button with Dropdown */}
      <div className='action-button-wrapper'>
        <XGButton
          id='share-button'
          startIcon={<ShareNetwork size={16} />}
          endIcon={<CaretDown size={12} />}
          text={t('common:share')}
          type='secondary'
          size='medium'
          variant='outlined'
          onClick={handleShareClick}
          className='action-button'
        />
        <Menu
          anchorEl={shareAnchor}
          open={Boolean(shareAnchor)}
          onClose={closeMenus}
          className='action-dropdown'
        >
          <MenuItem onClick={handleShareProposal}>
            <ShareNetwork size={16} className='menu-icon' />
            {t('trip:shareProposal')}
          </MenuItem>
          <MenuItem onClick={handleShareFinal}>
            <ShareNetwork size={16} className='menu-icon' />
            {t('trip:shareFinalItinerary')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleEmbedCode}>
            <Code size={16} className='menu-icon' />
            {t('trip:embedCode', 'Embed Code')}
          </MenuItem>
        </Menu>
      </div>

      {/* Export Button with Dropdown */}
      <div className='action-button-wrapper'>
        <XGButton
          id='export-button'
          startIcon={
            pdfIsLoading ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <FileArrowDown size={16} />
            )
          }
          endIcon={!pdfIsLoading && <CaretDown size={12} />}
          text={
            pdfIsLoading
              ? t('common:exporting' + '...', 'Exporting...')
              : t('common:export', 'Export')
          }
          type='secondary'
          size='medium'
          variant='outlined'
          onClick={handleExportClick}
          className={`action-button ${pdfIsLoading ? 'loading' : ''}`}
        />
        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={closeMenus}
          className='action-dropdown'
        >
          <MenuItem
            onClick={handleExportProposalPDF}
            disabled={pdfIsLoading}
            className={exportingType === 'proposal' ? 'loading' : ''}
          >
            {exportingType === 'proposal' ? (
              <CircularProgress size={16} className='menu-icon' />
            ) : (
              <FileText size={16} className='menu-icon' />
            )}
            {t('trip:proposalPdf', 'Proposal PDF')}
          </MenuItem>
          <MenuItem
            onClick={handleExportFinalPDF}
            disabled={pdfIsLoading}
            className={exportingType === 'final' ? 'loading' : ''}
          >
            {exportingType === 'final' ? (
              <CircularProgress size={16} className='menu-icon' />
            ) : (
              <FileText size={16} className='menu-icon' />
            )}
            {t('trip:finalItineraryPdf', 'Final Itinerary PDF')}
          </MenuItem>
          <MenuItem
            onClick={handleExportAttachments}
            disabled={pdfIsLoading}
            className={exportingType === 'attachments' ? 'loading' : ''}
          >
            {exportingType === 'attachments' ? (
              <CircularProgress size={16} className='menu-icon' />
            ) : (
              <Paperclip size={16} className='menu-icon' />
            )}
            {t('trip:exportAttachments', 'Export Attachments')}
          </MenuItem>
        </Menu>
      </div>

      {/* Preview Button with Badge and Dropdown */}
      <div className='action-button-wrapper preview-button-wrapper'>
        <div className='preview-button-container'>
          <XGButton
            id='preview-button'
            startIcon={<Eye size={16} />}
            text={t('common:preview')}
            type='primary'
            size='medium'
            variant='contained'
            onClick={openPreview}
            className='action-button preview-main-button'
          />
          <span className='preview-badge'>
            {previewMode === 'proposal'
              ? t('trip:proposal', 'Proposal')
              : t('trip:final', 'Final')}
          </span>
          <IconButton
            size='small'
            onClick={handlePreviewClick}
            className='preview-dropdown-trigger'
          >
            <CaretDown size={12} color='#fff' />
          </IconButton>
        </div>
        <Menu
          anchorEl={previewAnchor}
          open={Boolean(previewAnchor)}
          onClose={closeMenus}
          className='action-dropdown'
        >
          <MenuItem
            onClick={() => handlePreviewModeChange('proposal')}
            selected={previewMode === 'proposal'}
          >
            <Eye size={16} className='menu-icon' />
            {t('trip:previewProposal', 'Preview Proposal')}
          </MenuItem>
          <MenuItem
            onClick={() => handlePreviewModeChange('final')}
            selected={previewMode === 'final'}
          >
            <Eye size={16} className='menu-icon' />
            {t('trip:previewFinalItinerary', 'Preview Final Itinerary')}
          </MenuItem>
        </Menu>
      </div>

      {/* Kebab Menu */}
      <div className='action-button-wrapper action-header-kebab'>
        <IconButton
          onClick={handleKebabClick}
          className='kebab-button '
          size='medium'
        >
          <DotsThreeVertical size={20} />
        </IconButton>
        <Menu
          anchorEl={kebabAnchor}
          open={Boolean(kebabAnchor)}
          onClose={closeMenus}
          className='action-dropdown'
        >
          <MenuItem onClick={handleCopyTrip} disabled={isCopying}>
            {isCopying ? (
              <CircularProgress size={16} className='menu-icon' />
            ) : (
              <Copy size={16} className='menu-icon' />
            )}
            {t('trip:copyTrip', 'Copy Trip')}
          </MenuItem>
          <MenuItem
            onClick={handleDeleteTrip}
            disabled={isCopying}
            className='menu-icon--delete'
          >
            <Trash size={16} className='menu-icon menu-icon--delete' />
            {t('common:delete', 'Delete')}
          </MenuItem>
        </Menu>
      </div>

      {/* Share Modal */}
      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        tripId={tripid}
        type={shareModalType}
        isPublic={isPublic}
        setIsPublic={setIsPublic}
      />

      {/* Embed Modal */}
      <EmbedModal
        open={embedModalOpen}
        onClose={() => setEmbedModalOpen(false)}
        tripId={tripid}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        id='delete-trip-modal'
        isOpenModal={deleteModalOpen}
        title={t('trip:deleteTrip')}
        onConfirm={confirmDeleteTrip}
        onCancel={() => setDeleteModalOpen(false)}
        description={`${t('trip:deleteTripConfirmation')} ${t('common:thisActionCannotBeUndone')}`}
        isSubmitting={isDeleting}
      />
    </div>
  );
};

NewTripHeaderActions.propTypes = {
  id: PropTypes.string,
  isPublic: PropTypes.any,
  setIsPublic: PropTypes.any
};

export default NewTripHeaderActions;
